import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormModal } from "@/components/pawkt/FormModal";
import { toast } from "sonner";
import { Target, TrendingUp, Gauge, Layers, CheckCircle2, Clock, Plus, Pencil, Info, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const money = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const STATUS = {
  achieved: { label: "Achieved", cls: "bg-emerald-100 text-emerald-700" },
  on_or_ahead: { label: "On / ahead of pace", cls: "bg-emerald-100 text-emerald-700" },
  behind: { label: "Behind pace", cls: "bg-amber-100 text-amber-700" },
  period_ended: { label: "Period ended", cls: "bg-slate-100 text-slate-600" },
};
const nowPeriod = () => new Date().toISOString().slice(0, 7);

export default function BusinessPlanning() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(false);

  const { data: summary, isLoading, isError, error } = useQuery({
    queryKey: ["bp-summary"],
    queryFn: async () => (await api.get("/business-planning/summary")).data,
    retry: false,
  });

  const createGoal = useMutation({
    mutationFn: (b) => api.post("/business-planning/goals", b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bp-summary"] }); setModalOpen(false); toast.success("Goal created"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const goalId = summary?.progress?.goal?.id;
  const updateGoal = useMutation({
    mutationFn: (b) => api.patch(`/business-planning/goals/${goalId}`, b),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bp-summary"] }); setEditTarget(false); toast.success("Goal updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (isLoading) return <Loading />;
  if (isError) return (
    <div data-testid="business-planning-page">
      <PageHeader title="Business Planning" subtitle="Set a goal, track actual revenue, and see what can close the gap" />
      <div data-testid="bp-error" className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
        Actual revenue could not be calculated reliably, so no totals are shown. {apiErr(error)}
      </div>
    </div>
  );

  const p = summary?.progress;

  return (
    <div data-testid="business-planning-page">
      <PageHeader title="Business Planning" subtitle="Set a goal, track actual revenue, and see what can close the gap" />

      {!summary?.has_goal ? (
        <div data-testid="bp-empty" className="rounded-xl border border-slate-200 bg-white p-10 text-center">
          <Target className="mx-auto h-10 w-10 text-slate-300" />
          <h3 className="mt-3 font-display text-xl font-semibold text-slate-900">No goal for {summary?.period}</h3>
          <p className="mt-1 text-sm text-slate-500">Set a monthly revenue goal to start planning toward it.</p>
          <Button data-testid="bp-create-goal-btn" onClick={() => setModalOpen(true)} className="mt-4 gap-2 bg-slate-900 text-white"><Plus className="h-4 w-4" /> Set a revenue goal</Button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* HERO: goal + collected */}
          <div data-testid="bp-hero" className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{summary.period} · Revenue goal</div>
                <div className="mt-1 font-display text-4xl font-bold text-slate-900" data-testid="bp-target">{money(p.progress.target)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span data-testid="bp-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS[p.progress.status]?.cls || "bg-slate-100 text-slate-600"}`}>{STATUS[p.progress.status]?.label || p.progress.status}</span>
                <Button data-testid="bp-edit-target-btn" size="sm" variant="outline" onClick={() => setEditTarget(true)} className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-end justify-between text-sm">
                <span className="text-slate-500">Actual revenue <span className="text-[11px] text-slate-400">(Net Sales before GST — same as Dashboard)</span></span>
                <span className="font-semibold text-slate-800" data-testid="bp-actual-revenue">{money(p.progress.actual_revenue)} <span className="text-slate-400">/ {money(p.progress.target)}</span></span>
              </div>
              <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, p.progress.pct_achieved)}%` }} data-testid="bp-progress-bar" />
              </div>
              <div className="mt-2 flex justify-between text-xs text-slate-500">
                <span>{p.progress.pct_achieved}% of target</span>
                <span data-testid="bp-remaining">{money(p.progress.remaining)} remaining to target</span>
              </div>
              <div className="mt-3 text-xs text-slate-400" data-testid="bp-cash-collected">Cash collected this month (separate, net of refunds): {money(p.collected.collected)}</div>
            </div>
          </div>

          {/* PACE */}
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard testid="bp-required-pace" icon={Gauge} label="Required pace" value={`${money(p.progress.required_pace)}/day`} sub={`${p.period.days_remaining} days left`} />
            <StatCard testid="bp-current-pace" icon={TrendingUp} label="Current revenue pace" value={`${money(p.progress.current_pace)}/day`} sub={`${p.period.days_elapsed} days elapsed`} />
            <StatCard testid="bp-projected" icon={Clock} label="Projected (at current pace)" value={money(p.progress.projected)} sub={p.progress.status === "behind" ? "Below target" : "On track"} />
          </div>

          {/* DEPARTMENT PLAN (billed) */}
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="bp-departments">
            <div className="mb-1 flex items-center gap-2"><Layers className="h-5 w-5 text-slate-400" /><h3 className="font-display text-lg font-semibold text-slate-900">Where revenue is generated</h3></div>
            <p className="mb-4 text-xs text-slate-400">Actual revenue by department (after discounts, before GST). Departments add up to actual revenue. Unallocated target: {money(p.unallocated)}.</p>
            <div className="space-y-2.5">
              {p.departments.length === 0 && <div className="text-sm text-slate-400">No department targets or billing yet.</div>}
              {p.departments.map((d) => (
                <div key={d.category} data-testid={`bp-dept-${d.category}`} className="rounded-lg border border-slate-200 px-3.5 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize text-slate-700">{d.category}</span>
                    <span className="text-slate-600">Actual {money(d.billed)}{d.has_target ? <> / target {money(d.target)}</> : <span className="text-[11px] text-slate-400"> · no target</span>}</span>
                  </div>
                  {d.has_target && (
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width: `${d.target > 0 ? Math.min(100, (d.billed / d.target) * 100) : 0}%` }} />
                    </div>
                  )}
                  {d.has_target && d.gap > 0 && <div className="mt-1 text-xs text-amber-600">Gap {money(d.gap)}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* GAP + CONTRIBUTORS */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="bp-gap">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">Closing the gap</h3>
              <Row label="Remaining to target" value={money(p.gap.remaining_to_target)} strong />
              <Row label="Potential additional revenue (estimate)" value={money(p.gap.potential_additional_revenue)} cls="text-indigo-600" />
              <div className="pl-3 text-xs text-slate-400">
                <Row label="from scheduled bookings" value={money(p.gap.scheduled_bookings)} />
                <Row label="from identified opportunities" value={p.gap.potential_quantified > 0 ? money(p.gap.potential_quantified) : "—"} />
              </div>
              <div className="my-2 border-t border-slate-100" />
              <Row label="Still to find after potential" value={money(p.gap.remaining_after_potential)} strong cls="text-amber-700" />
              <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-400"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Potential additional revenue is an estimate. It is never added to actual revenue and is not guaranteed.</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="bp-contributors">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">Recommended focus</h3>
              <p className="mb-3 text-xs text-slate-400">Services to prioritise, with their estimated contribution toward the remaining target.</p>
              {(p.recommended_focus || []).map((f) => (
                <div key={f.category} data-testid={`bp-focus-${f.category}`} className="flex items-center justify-between gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
                  <span className="capitalize text-slate-700">{f.category}<span className="block text-[11px] normal-case text-slate-400">{[f.scheduled_bookings_value > 0 && `${money(f.scheduled_bookings_value)} scheduled`, f.opportunity_count > 0 && `${f.opportunity_count} opportunities`, f.department_gap > 0 && `${money(f.department_gap)} below department target`].filter(Boolean).join(" · ")}</span></span>
                  <span className="text-right font-medium text-indigo-600">{f.estimated_contribution > 0 ? `~${money(f.estimated_contribution)}` : "not estimated"}{f.share_of_remaining_pct > 0 && <span className="block text-[11px] font-normal text-slate-400">{f.share_of_remaining_pct}% of remaining</span>}</span>
                </div>
              ))}
              {(p.recommended_focus || []).length === 0 && <div className="text-sm text-slate-400">No scheduled bookings, open opportunities or department gaps identified yet.</div>}
            </div>
          </div>

          {/* cost note */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500" data-testid="bp-cost">
            {p.cost.available ? <>Recorded expenses this period: <strong>{money(p.cost.expenses)}</strong> — <span className="text-xs">{p.cost.note}</span></> : p.cost.note}
          </div>

          {/* PLAN EXECUTION (flag business_plan_execution) */}
          <PlanExecution goalId={p.goal?.id} />
        </div>
      )}

      {/* FINANCIAL PICTURE (flag profit_intelligence) + WHAT CHANGED (flag profit_intelligence_analysis).
          Rendered independently of goal state — financial truth does not require a revenue goal. */}
      <div className="mt-5 space-y-5">
        <BusinessReview />
        <FinancialPicture />
        <FinancialChange />
        <ResourceCostIntelligence />
      </div>

      {/* create goal modal */}
      <FormModal open={modalOpen} onOpenChange={setModalOpen} title="Set revenue goal" testid="bp-goal-modal"
        initial={{ period: nowPeriod(), target: "" }} submitLabel="Create goal"
        fields={[
          { name: "target", label: "Monthly revenue target (₹)", type: "number", full: true },
          { name: "period", label: "Period (YYYY-MM)", full: true },
        ]}
        onSubmit={(v) => createGoal.mutateAsync({ goal_type: "revenue", target: Number(v.target), period: v.period })} />

      {/* edit target modal */}
      <FormModal open={editTarget} onOpenChange={setEditTarget} title="Edit goal target" testid="bp-edit-modal"
        initial={{ target: p?.progress?.target || "" }} submitLabel="Save"
        fields={[{ name: "target", label: "Monthly revenue target (₹)", type: "number", full: true }]}
        onSubmit={(v) => updateGoal.mutateAsync({ target: Number(v.target) })} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><Icon className="h-4 w-4 text-slate-400" /> {label}</div>
      <div className="mt-1.5 font-display text-2xl font-bold text-slate-900">{value}</div>
      <div className="text-xs text-slate-400">{sub}</div>
    </div>
  );
}

function Row({ label, value, strong, cls = "" }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className={strong ? "font-medium text-slate-700" : "text-slate-500"}>{label}</span>
      <span className={`${strong ? "font-semibold" : "font-medium"} ${cls || "text-slate-800"}`}>{value}</span>
    </div>
  );
}

const PLAN_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  active: { label: "Active", cls: "bg-sky-100 text-sky-700" },
  on_track: { label: "On track", cls: "bg-emerald-100 text-emerald-700" },
  at_risk: { label: "At risk", cls: "bg-amber-100 text-amber-700" },
  behind: { label: "Behind", cls: "bg-rose-100 text-rose-700" },
  paused: { label: "Paused", cls: "bg-slate-100 text-slate-600" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500" },
};

function PlanExecution({ goalId }) {
  const qc = useQueryClient();
  const [addItem, setAddItem] = useState(false);
  const { data: list, isError } = useQuery({
    queryKey: ["bp-plans", goalId],
    queryFn: async () => (await api.get(`/business-plans?goal_id=${goalId}`)).data,
    enabled: !!goalId, retry: false,
  });
  const plan = list?.items?.find((x) => ["active", "draft", "on_track", "at_risk", "behind"].includes(x.status)) || list?.items?.[0];
  const { data: perf } = useQuery({
    queryKey: ["bp-plan", plan?.id],
    queryFn: async () => (await api.get(`/business-plans/${plan.id}`)).data,
    enabled: !!plan?.id, retry: false,
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["bp-plans"] }); qc.invalidateQueries({ queryKey: ["bp-plan"] }); };
  const createPlan = useMutation({ mutationFn: () => api.post("/business-plans", { goal_id: goalId, title: "Revenue Recovery Plan" }),
    onSuccess: () => { invalidate(); toast.success("Plan created"); }, onError: (e) => toast.error(apiErr(e)) });
  const setStatus = useMutation({ mutationFn: (s) => api.patch(`/business-plans/${plan.id}`, { status: s }),
    onSuccess: () => { invalidate(); }, onError: (e) => toast.error(apiErr(e)) });
  const addItemM = useMutation({ mutationFn: (b) => api.post(`/business-plans/${plan.id}/items`, b),
    onSuccess: () => { invalidate(); setAddItem(false); toast.success("Plan item added"); }, onError: (e) => toast.error(apiErr(e)) });
  const addTask = useMutation({ mutationFn: (iid) => api.post(`/business-plans/${plan.id}/items/${iid}/actions`, { kind: "task" }),
    onSuccess: () => { invalidate(); toast.success("Task created"); }, onError: (e) => toast.error(apiErr(e)) });

  if (isError) return null; // execution flag off => endpoint 403

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="plan-execution-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">Plan — how we close the gap</h3>
        {plan && <span data-testid="bp-plan-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${PLAN_STATUS[perf?.status || plan.status]?.cls || "bg-slate-100 text-slate-600"}`}>{PLAN_STATUS[perf?.status || plan.status]?.label || plan.status}</span>}
      </div>
      {!plan ? (
        <div className="py-4 text-center">
          <p className="text-sm text-slate-500">No plan yet. Create one to turn the gap into assignable actions.</p>
          <Button data-testid="bp-create-plan-btn" onClick={() => createPlan.mutate()} className="mt-3 gap-2 bg-slate-900 text-white"><Plus className="h-4 w-4" /> Create plan</Button>
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Mini label="Planned" value={money(perf?.planned_contribution || 0)} testid="bp-plan-planned" />
            <Mini label="Observed (linked)" value={money(perf?.observed_contribution || 0)} testid="bp-plan-observed" />
            <Mini label="Uncovered gap" value={money(perf?.remaining_uncovered || 0)} />
            <Mini label="Actions" value={`${perf?.actions?.completed || 0}/${perf?.actions?.total || 0} done`} sub={perf?.actions?.overdue ? `${perf.actions.overdue} overdue` : ""} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="bp-add-item-btn" size="sm" variant="outline" onClick={() => setAddItem(true)} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add plan item</Button>
            {["draft"].includes(plan.status) && <Button data-testid="bp-activate-plan-btn" size="sm" onClick={() => setStatus.mutate("active")} className="bg-slate-900 text-white">Activate plan</Button>}
          </div>
          <div className="mt-4 space-y-2.5">
            {(perf?.items || []).filter((it) => it.status !== "cancelled").map((it) => (
              <div key={it.id} data-testid={`bp-item-${it.id}`} className="rounded-lg border border-slate-200 px-3.5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{it.title}</div>
                    <div className="mt-0.5 text-xs text-slate-400">Planned {money(it.planned_contribution)} · Observed {money(it.observed_contribution)} · {it.actions.completed}/{it.actions.total} actions {it.source?.type !== "manual" && `· from ${it.source?.type}`}</div>
                  </div>
                  <Button data-testid={`bp-add-task-${it.id}`} size="sm" variant="ghost" onClick={() => addTask.mutate(it.id)} className="shrink-0 gap-1 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Add task</Button>
                </div>
              </div>
            ))}
            {(perf?.items || []).length === 0 && <div className="text-sm text-slate-400">No plan items yet. Add one to start executing.</div>}
          </div>
          <p className="mt-3 text-xs text-slate-400">Observed = collected revenue linked to each item's customers (not causally attributed). Planned values are never rewritten to match results.</p>
        </>
      )}

      <FormModal open={addItem} onOpenChange={setAddItem} title="Add plan item" testid="bp-item-modal"
        initial={{ title: "", planned_contribution: "" }} submitLabel="Add item"
        fields={[
          { name: "title", label: "What needs to happen?", full: true },
          { name: "planned_contribution", label: "Planned contribution (₹, optional)", type: "number", full: true },
        ]}
        onSubmit={(v) => addItemM.mutateAsync({ title: v.title, planned_contribution: Number(v.planned_contribution || 0) })} />
    </div>
  );
}

function Mini({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 font-display text-lg font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-amber-600">{sub}</div>}
    </div>
  );
}

const DQ = { COMPLETE: "bg-emerald-100 text-emerald-700", AVAILABLE: "bg-emerald-100 text-emerald-700", PARTIAL: "bg-amber-100 text-amber-700", UNAVAILABLE: "bg-slate-100 text-slate-500" };

const SEV = {
  CRITICAL: "bg-red-100 text-red-700",
  REVIEW: "bg-amber-100 text-amber-700",
  INFO: "bg-slate-100 text-slate-500",
};
const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}₹${Math.abs(Math.round(n || 0)).toLocaleString("en-IN")}`;
const pct = (d) => (d.percentage_change === null || d.percentage_change === undefined ? "—" : `${d.percentage_change > 0 ? "+" : ""}${d.percentage_change.toFixed(1)}%`);

function DeltaChip({ label, d, good = "up", testid }) {
  const up = d.absolute_change > 0;
  const positive = (good === "up" && up) || (good === "down" && d.absolute_change < 0);
  const neutral = d.absolute_change === 0;
  const cls = neutral ? "text-slate-500" : positive ? "text-emerald-600" : "text-rose-600";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-0.5 font-display text-base font-semibold text-slate-900">{money(d.current)}</div>
      <div className={`mt-0.5 text-xs font-semibold ${cls}`}>{signed(d.absolute_change)} · {pct(d)}</div>
      <div className="text-[11px] text-slate-400">was {money(d.previous)}</div>
    </div>
  );
}

function FinancialChange() {
  const [evidence, setEvidence] = useState(null); // {title, items:[{type,label,department,branch_id}]}
  const { data, isError } = useQuery({
    queryKey: ["profit-analysis"],
    queryFn: async () => (await api.get("/profit-intelligence/analysis")).data,
    retry: false,
  });
  if (isError || !data) return null; // flag off / no permission => hidden
  const m = data.metrics;
  const comps = data.drivers?.components || [];
  const depChanges = (data.departments || []).filter((d) => Math.abs(d.absolute_change) > 0);
  const DRIVER_EV = {
    collected_revenue: [{ type: "payments", label: "Payments (collected)" }, { type: "refunds", label: "Refunds" }],
    recorded_cost: [{ type: "expenses", label: "Recorded expenses" }, { type: "commissions", label: "Commissions" }, { type: "cogs", label: "Inventory / COGS" }],
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="financial-change-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">What changed</h3>
        <span data-testid="fc-expenses-quality" className={`rounded-full px-3 py-1 text-xs font-semibold ${DQ[data.data_quality.expenses]}`}>Expense data: {data.data_quality.expenses}</span>
      </div>
      <p className="mb-3 text-xs text-slate-400">{data.period.label} vs {data.comparison_period.label} · recorded data only</p>
      <div className="rounded-lg bg-slate-50 p-3 text-sm font-medium text-slate-700" data-testid="fc-headline">{data.headline}</div>

      {/* WHAT CHANGED */}
      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <DeltaChip label="Recorded contribution" d={m.contribution} good="up" testid="fc-contribution" />
        <DeltaChip label="Collected revenue" d={m.collected} good="up" testid="fc-collected" />
        <DeltaChip label="Recorded costs" d={m.recorded_costs} good="down" testid="fc-costs" />
        <DeltaChip label="Outstanding" d={m.outstanding} good="down" testid="fc-outstanding" />
      </div>

      {/* WHY — drivers */}
      {Math.abs(m.contribution.absolute_change) > 0 && (
        <div className="mt-4" data-testid="fc-drivers">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Why contribution changed</div>
          <div className="mt-2 space-y-1.5">
            {comps.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span className="capitalize text-slate-600">{c.driver.replace(/_/g, " ")}</span>
                <div className="flex items-center gap-3">
                  <span className={`font-semibold ${c.effect_on_contribution >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{signed(c.effect_on_contribution)}</span>
                  {DRIVER_EV[c.driver] && Math.abs(c.change) > 0 && (
                    <button data-testid={`fc-driver-review-${c.driver}`} onClick={() => setEvidence({ title: `${c.driver.replace(/_/g, " ")} — evidence`, items: DRIVER_EV[c.driver] })}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"><Search className="h-3 w-3" /> Review</button>
                  )}
                </div>
              </div>
            ))}
            <p className="pt-1 text-[11px] text-slate-400">Recorded contribution change = collected change − recorded cost change. Refunds are already within collected (never counted twice).</p>
          </div>
        </div>
      )}

      {/* WHERE — department revenue changes */}
      {depChanges.length > 0 && (
        <div className="mt-4" data-testid="fc-departments">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Where (billed revenue by department)</div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {depChanges.map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`fc-dept-${d.department}`}>
                <span className="capitalize text-slate-600">{d.department}</span>
                <span className={`font-semibold ${d.absolute_change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{signed(d.absolute_change)}</span>
              </div>
            ))}
          </div>
          <p className="pt-1 text-[11px] text-slate-400">Department costs are unallocated — contribution per department can't be determined.</p>
        </div>
      )}

      {/* SIGNALS */}
      {(data.signals || []).length > 0 && (
        <div className="mt-4 space-y-2" data-testid="fc-signals">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Signals to review</div>
          {data.signals.map((s, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3" data-testid={`fc-signal-${i}`}>
              <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEV[s.severity]}`}>{s.severity}</span>
              <span className="flex-1 text-xs text-slate-600">{s.message}</span>
              {(s.evidence_for || []).length > 0 && (
                <button data-testid={`fc-signal-review-${i}`} onClick={() => setEvidence({ title: "Signal evidence", items: s.evidence_for })}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"><Search className="h-3 w-3" /> Review</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* DATA LIMITATIONS */}
      {(data.data_limitations || []).length > 0 && (
        <div className="mt-4" data-testid="fc-limitations">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data limitations</div>
          <div className="mt-2 space-y-1.5">
            {data.data_limitations.map((w, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />{w}</div>
            ))}
          </div>
        </div>
      )}

      <EvidenceDrawer evidence={evidence} onClose={() => setEvidence(null)} />
    </div>
  );
}

const EV_LABEL = { payments: "Payments", refunds: "Refunds", expenses: "Expenses", commissions: "Commissions", cogs: "Inventory / COGS", invoices: "Invoices", outstanding: "Outstanding", bookings: "Bookings" };

function ResourceCostIntelligence() {
  const [evidence, setEvidence] = useState(null);
  const { data, isError } = useQuery({
    queryKey: ["resource-cost"],
    queryFn: async () => (await api.get("/profit-intelligence/resources")).data,
    retry: false,
  });
  if (isError || !data) return null; // flag off / no permission => hidden
  const ct = data.recorded_cost_total;
  const cats = (data.cost_categories || []).filter((c) => c.current > 0).slice(0, 8);
  const g = data.cost_groups;
  const act = data.resources?.activity?.current || {};
  const maxShare = Math.max(1, ...cats.map((c) => c.share));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="resource-cost-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">Where money goes</h3>
        <span data-testid="rc-expenses-quality" className={`rounded-full px-3 py-1 text-xs font-semibold ${DQ[data.data_quality.expenses]}`}>Expense data: {data.data_quality.expenses}</span>
      </div>
      <p className="mb-3 text-xs text-slate-400">{data.period.label} vs {data.comparison_period.label} · recorded costs only · costs are UNALLOCATED unless explicitly assigned to a department</p>

      <div className="rounded-lg bg-slate-50 p-3 text-sm" data-testid="rc-cost-total">
        <span className="font-semibold text-slate-700">Recorded costs {money(ct.current)}</span>
        <span className={`ml-2 font-semibold ${ct.absolute_change > 0 ? "text-rose-600" : "text-emerald-600"}`}>{signed(ct.absolute_change)} · {pct(ct)}</span>
        <span className="ml-1 text-slate-400">(was {money(ct.previous)})</span>
      </div>

      {/* allocation split */}
      {data.cost_allocation && (
        <div className="mt-3" data-testid="rc-allocation">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="rc-allocated">
              <div className="text-xs text-emerald-700">Allocated to departments</div>
              <div className="font-display text-base font-semibold text-slate-900">{money(data.cost_allocation.allocated_total.current)}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="rc-unallocated">
              <div className="text-xs text-slate-500">Unallocated</div>
              <div className="font-display text-base font-semibold text-slate-900">{money(data.cost_allocation.unallocated_total.current)}</div>
            </div>
          </div>
          {(data.cost_allocation.by_department || []).length > 0 && (
            <div className="mt-2 space-y-1" data-testid="rc-allocation-depts">
              {data.cost_allocation.by_department.map((d, i) => (
                <div key={i} className="flex justify-between text-xs" data-testid={`rc-alloc-${d.department}`}>
                  <span className="capitalize text-slate-600">{d.department}</span>
                  <span className="text-slate-500">{money(d.current)} · {d.share}%{Math.abs(d.absolute_change) > 0 ? ` · ${signed(d.absolute_change)}` : ""}</span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-1 text-[11px] text-slate-400">Unallocated is shown separately and never redistributed across departments.</p>
        </div>
      )}

      {/* cost concentration */}
      <div className="mt-4" data-testid="rc-categories">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cost concentration</div>
        <div className="mt-2 space-y-1.5">
          {cats.map((c, i) => (
            <div key={i} data-testid={`rc-cat-${c.category}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="capitalize text-slate-600">{c.category}</span>
                <span className="text-slate-500">{money(c.current)} · {c.share}%{Math.abs(c.absolute_change) > 0 ? ` · ${signed(c.absolute_change)}` : ""}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.round((c.share / maxShare) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* cost groups */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="rc-groups">
        <div className="rounded-xl border border-slate-200 p-3" data-testid="rc-group-staff">
          <div className="text-xs text-slate-400">Staff cost (salaries + commissions)</div>
          <div className="mt-0.5 font-display text-base font-semibold text-slate-900">{money(g.staff.current)}</div>
          <div className={`text-xs font-semibold ${g.staff.absolute_change > 0 ? "text-rose-600" : "text-emerald-600"}`}>{signed(g.staff.absolute_change)}</div>
          <div className="mt-1 text-[11px] text-slate-400">Configured monthly: {money(g.staff.configured_monthly_staff_cost)} · {g.staff.headcount} staff</div>
        </div>
        <div className="rounded-xl border border-slate-200 p-3" data-testid="rc-group-inventory">
          <div className="text-xs text-slate-400">Inventory / COGS</div>
          <div className="mt-0.5 font-display text-base font-semibold text-slate-900">{money(g.inventory_cogs.current)}</div>
          <div className={`text-xs font-semibold ${g.inventory_cogs.absolute_change > 0 ? "text-rose-600" : "text-emerald-600"}`}>{signed(g.inventory_cogs.absolute_change)}</div>
          {!g.inventory_cogs.complete && <div className="mt-1 text-[11px] text-amber-600">Partial — some unit costs missing</div>}
        </div>
        <div className="rounded-xl border border-slate-200 p-3" data-testid="rc-group-marketing">
          <div className="text-xs text-slate-400">Marketing</div>
          {g.marketing.available ? (
            <>
              <div className="mt-0.5 font-display text-base font-semibold text-slate-900">{money(g.marketing.current)}</div>
              <div className={`text-xs font-semibold ${g.marketing.absolute_change > 0 ? "text-rose-600" : "text-emerald-600"}`}>{signed(g.marketing.absolute_change)}</div>
              <div className="mt-1 text-[11px] text-slate-400">ROI not calculated (no attribution)</div>
            </>
          ) : <div className="mt-0.5 text-sm text-slate-400">Unavailable</div>}
        </div>
      </div>

      {/* resource activity */}
      <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="rc-activity">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Resource activity</div>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500" data-testid="rc-capacity-status">Capacity utilization: UNAVAILABLE</span>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
          <div><div className="font-display text-base font-semibold text-slate-900">{act.completed_count || 0}</div><div className="text-slate-400">completed</div></div>
          <div><div className="font-display text-base font-semibold text-slate-900">{act.completed_hours || 0}h</div><div className="text-slate-400">service-hours</div></div>
          <div><div className="font-display text-base font-semibold text-slate-900">{(act.cancelled_count || 0) + (act.no_show_count || 0)}</div><div className="text-slate-400">cancelled / no-show</div></div>
        </div>
        {(data.resources?.inventory || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data.resources.inventory.map((r, i) => (
              <span key={i} className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500" data-testid={`rc-resource-${r.type}`}>{r.count}× {r.type.replace(/_/g, " ")} (cap {r.capacity})</span>
            ))}
          </div>
        )}
      </div>

      {/* capacity (PAWKT-CAPACITY-INTELLIGENCE-001) */}
      {data.resources?.capacity && (() => {
        const c = data.resources.capacity;
        const u = c.utilization;
        const avail = c.available_capacity.hours;
        return (
          <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="rc-capacity">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Capacity</div>
              <span data-testid="rc-capacity-dq" className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DQ[c.data_quality.status]}`}>{c.data_quality.status}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4">
              <div data-testid="rc-cap-window"><div className="font-display text-base font-semibold text-slate-900">{c.configured_operating_window.status === "AVAILABLE" ? `${c.configured_operating_window.hours}h` : "—"}</div><div className="text-slate-400">operating window</div></div>
              <div data-testid="rc-cap-available"><div className="font-display text-base font-semibold text-slate-900">{avail != null ? `${avail}h` : "—"}</div><div className="text-slate-400">available</div></div>
              <div data-testid="rc-cap-booked"><div className="font-display text-base font-semibold text-slate-900">{c.booked.service_hours}h</div><div className="text-slate-400">booked</div></div>
              <div data-testid="rc-cap-completed"><div className="font-display text-base font-semibold text-slate-900">{c.completed.service_hours}h</div><div className="text-slate-400">completed</div></div>
            </div>
            {u.status === "AVAILABLE" ? (
              <div className="mt-3" data-testid="rc-cap-utilization">
                <div className="flex items-center justify-between text-xs text-slate-500"><span>Booked utilization</span><span className="font-semibold text-slate-800">{u.booked_pct}%</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.min(100, u.booked_pct)}%` }} /></div>
                <div className="mt-1 text-[11px] text-slate-400">Completed utilization {u.completed_pct}% · unused {c.unused.hours}h</div>
              </div>
            ) : (
              <div className="mt-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-500" data-testid="rc-cap-unavailable">Utilization UNAVAILABLE — {u.reason}</div>
            )}
            <div className="mt-2 text-[11px] text-slate-400" data-testid="rc-cap-note">Cancelled {c.cancelled.service_hours}h · no-show {c.no_show.service_hours}h (separate — not counted as unused). {c.assumptions[0]}</div>
          </div>
        );
      })()}

      {/* signals */}
      {(data.signals || []).length > 0 && (
        <div className="mt-4 space-y-2" data-testid="rc-signals">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cost signals to review</div>
          {data.signals.map((s, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3" data-testid={`rc-signal-${i}`}>
              <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEV[s.severity]}`}>{s.severity}</span>
              <span className="flex-1 text-xs text-slate-600">{s.message}</span>
              {(s.evidence_for || []).length > 0 && (
                <button data-testid={`rc-signal-review-${i}`} onClick={() => setEvidence({ title: "Cost evidence", items: s.evidence_for })}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50"><Search className="h-3 w-3" /> Review</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* data limitations */}
      {(data.data_limitations || []).length > 0 && (
        <div className="mt-4" data-testid="rc-limitations">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data limitations</div>
          <div className="mt-2 space-y-1.5">
            {data.data_limitations.map((w, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />{w}</div>
            ))}
          </div>
        </div>
      )}

      <EvidenceDrawer evidence={evidence} onClose={() => setEvidence(null)} />
    </div>
  );
}

function EvidenceDrawer({ evidence, onClose }) {
  const items = evidence?.items || [];
  const [type, setType] = useState(items[0]?.type);
  const [which, setWhich] = useState("current");
  const [limit, setLimit] = useState(25);
  // reset active tab whenever a new signal is opened
  const activeType = items.find((it) => it.type === type) ? type : items[0]?.type;
  const tab = items.find((it) => it.type === activeType) || {};

  const { data, isFetching } = useQuery({
    queryKey: ["evidence", activeType, which, limit, tab.department, tab.branch_id],
    queryFn: async () => (await api.get("/profit-intelligence/evidence", {
      params: { evidence_type: activeType, which, limit, department: tab.department || "", branch_id: tab.branch_id || "" },
    })).data,
    enabled: !!evidence && !!activeType,
  });

  return (
    <Sheet open={!!evidence} onOpenChange={(o) => { if (!o) { onClose(); setType(items[0]?.type); setWhich("current"); setLimit(25); } }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md" data-testid="evidence-drawer">
        <SheetHeader>
          <SheetTitle className="font-display">{evidence?.title || "Evidence"}</SheetTitle>
          <SheetDescription className="text-xs text-slate-400">Read-only canonical records behind this signal. Same period and scope as the analysis.</SheetDescription>
        </SheetHeader>

        {/* period toggle */}
        <div className="mt-3 inline-flex rounded-lg border border-slate-200 p-0.5 text-xs" data-testid="evidence-period-toggle">
          {["current", "comparison"].map((w) => (
            <button key={w} onClick={() => { setWhich(w); setLimit(25); }} data-testid={`evidence-period-${w}`}
              className={`rounded-md px-3 py-1 font-semibold capitalize ${which === w ? "bg-slate-900 text-white" : "text-slate-500"}`}>{w}</button>
          ))}
        </div>

        {/* evidence type tabs */}
        {items.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="evidence-tabs">
            {items.map((it) => (
              <button key={it.type} onClick={() => { setType(it.type); setLimit(25); }} data-testid={`evidence-tab-${it.type}`}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold ${activeType === it.type ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>{EV_LABEL[it.type] || it.type}</button>
            ))}
          </div>
        )}

        {/* records */}
        <div className="mt-4" data-testid="evidence-body">
          {!data ? (
            <div className="py-8 text-center text-sm text-slate-400">{isFetching ? "Loading…" : "—"}</div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700" data-testid="evidence-summary">{data.total_count} {data.label?.toLowerCase() || activeType} · {money(data.total_amount)}</span>
                <span className="text-slate-400">{data.period.label}</span>
              </div>
              {data.data_limitation && (
                <div className="mb-2 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-700" data-testid="evidence-limitation">{data.data_limitation}</div>
              )}
              {data.records.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400" data-testid="evidence-empty">No records for this period.</div>
              ) : (
                <div className="space-y-1.5">
                  {data.records.map((r, i) => <EvidenceRow key={r.id || i} type={activeType} r={r} i={i} />)}
                </div>
              )}
              {data.page?.has_more && (
                <button data-testid="evidence-load-more" onClick={() => setLimit((l) => l + 25)}
                  className="mt-3 w-full rounded-lg border border-slate-200 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50">Load more</button>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EvidenceRow({ type, r, i }) {
  const date = (r.date || "").slice(0, 10);
  const Line = ({ children }) => <div className="rounded-lg border border-slate-200 px-3 py-2 text-xs" data-testid={`evidence-row-${i}`}>{children}</div>;
  if (type === "payments")
    return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700">{money(r.amount)}</span><span className="text-slate-400">{date}</span></div><div className="mt-0.5 text-slate-500">{r.method} · {r.invoice_number || "—"}{r.customer ? ` · ${r.customer}` : ""}</div></Line>;
  if (type === "refunds")
    return <Line><div className="flex justify-between"><span className="font-semibold text-rose-600">−{money(r.amount)}</span><span className="text-slate-400">{date}</span></div><div className="mt-0.5 text-slate-500">{r.reason || "refund"}{r.invoice_id ? ` · ${r.invoice_id}` : ""}</div></Line>;
  if (type === "expenses")
    return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700 capitalize">{r.category}</span><span className="text-slate-400">{date}</span></div><div className="mt-0.5 flex justify-between text-slate-500"><span>{money(r.amount)}</span><span className="text-[10px] font-semibold uppercase text-slate-400">{r.allocation}</span></div></Line>;
  if (type === "commissions")
    return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700">{money(r.amount)}</span><span className="text-slate-400">{date}</span></div><div className="mt-0.5 text-slate-500">{r.staff || "—"}{r.service_ref ? ` · ${r.service_ref}` : ""}</div></Line>;
  if (type === "cogs")
    return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700">{r.product}</span><span>{money(r.cost)}</span></div><div className="mt-0.5 text-slate-500">qty {r.qty} × {money(r.unit_cost)}{!r.cost_known ? " · no unit cost recorded" : ""}</div></Line>;
  if (type === "outstanding")
    return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700">{r.invoice_number || r.id}</span><span className="font-semibold text-amber-700">{money(r.outstanding)}</span></div><div className="mt-0.5 text-slate-500">{r.customer || "—"} · billed {money(r.billed)} · paid {money(r.paid)}</div></Line>;
  // invoices
  return <Line><div className="flex justify-between"><span className="font-semibold text-slate-700">{r.invoice_number || r.id}</span><span>{money(r.total)}</span></div><div className="mt-0.5 flex justify-between text-slate-500"><span>{r.customer || "—"} · {(r.categories || []).join(", ")}</span><span className="capitalize">{r.status}</span></div></Line>;
}

const COVERAGE = {
  GOAL_REACHED: { label: "Goal reached", cls: "bg-emerald-100 text-emerald-700" },
  COVERED: { label: "Covered", cls: "bg-emerald-100 text-emerald-700" },
  PARTIALLY_COVERED: { label: "Partially covered", cls: "bg-amber-100 text-amber-700" },
  UNCOVERED: { label: "Uncovered", cls: "bg-rose-100 text-rose-700" },
  NO_ACTIVE_PLAN: { label: "No active plan", cls: "bg-slate-100 text-slate-600" },
  NO_GOAL: { label: "No goal", cls: "bg-slate-100 text-slate-500" },
};

function BusinessReview() {
  const { data, isError } = useQuery({
    queryKey: ["business-review"],
    queryFn: async () => (await api.get("/business-planning/review")).data,
    retry: false,
  });
  if (isError || !data) return null; // flag off / no permission => hidden
  if (!data.has_goal) return null;   // no goal => BP-003 shows nothing (goal section handles empty)
  const cov = COVERAGE[data.coverage_status] || COVERAGE.NO_GOAL;
  const cap = data.capacity_context || {};
  const fin = data.financial_context || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="business-review-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">Business review</h3>
        <span data-testid="br-coverage-status" className={`rounded-full px-3 py-1 text-xs font-semibold ${cov.cls}`}>{cov.label}</span>
      </div>
      <p className="mb-3 text-xs text-slate-400">Coverage compares your remaining goal to the plan's remaining planned contribution. This is a review of recorded facts — not a forecast.</p>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="br-metrics">
        <Mini label="Goal" value={money(data.goal_target)} testid="br-goal" />
        <Mini label="Actual revenue" value={money(data.actual_revenue)} testid="br-actual-revenue" />
        <Mini label="Remaining" value={money(data.remaining_goal)} testid="br-remaining" />
        <Mini label="Plan covers" value={money(data.remaining_planned_contribution)} testid="br-planned" />
        <Mini label="Uncovered" value={money(data.uncovered_amount)} testid="br-uncovered" />
      </div>

      {data.coverage_status === "NO_ACTIVE_PLAN" ? (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500" data-testid="br-no-plan">No active business plan is currently linked to this goal.</div>
      ) : data.coverage_status === "GOAL_REACHED" ? (
        <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700" data-testid="br-reached">Goal reached based on actual revenue.</div>
      ) : (data.items || []).length > 0 && (
        <div className="mt-4" data-testid="br-items">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Plan performance (planned vs observed)</div>
          <div className="mt-2 space-y-1.5">
            {data.items.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs" data-testid={`br-item-${it.id}`}>
                <span className="flex-1 truncate font-medium text-slate-700">{it.title}{it.category ? ` · ${it.category}` : ""}</span>
                <span className="text-slate-500">{money(it.planned_contribution)} planned</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{money(it.observed_contribution)} observed</span>
                <span className={`font-semibold ${it.variance < 0 ? "text-rose-600" : "text-emerald-600"}`}>{it.variance >= 0 ? "+" : "−"}{money(Math.abs(it.variance))}</span>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Observed is collected revenue linked to the item's customers — it does not establish causality.</p>
        </div>
      )}

      {(data.review_items || []).length > 0 && (
        <div className="mt-4" data-testid="br-review-items">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">What to review</div>
          <div className="mt-2 space-y-1.5">
            {data.review_items.map((ri, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-xs" data-testid={`br-review-${ri.type}`}>
                <span className={`mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ri.severity === "REVIEW" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{ri.severity}</span>
                <span className="flex-1 text-slate-600">{ri.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {data.plan_id && (
          <a href="/business-planning" data-testid="br-review-plan-cta" className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800">Review current plan</a>
        )}
        {cap.status && cap.status !== "UNAVAILABLE" && (
          <span className="text-[11px] text-slate-500" data-testid="br-capacity-context">{cap.message}</span>
        )}
        {fin.available && (
          <span className="text-[11px] text-slate-500" data-testid="br-financial-context">Recorded cost {money(fin.recorded_cost)} · contribution {money(fin.recorded_contribution)} · outstanding {money(fin.outstanding)}</span>
        )}
      </div>

      {(data.data_limitations || []).length > 0 && (
        <div className="mt-4" data-testid="br-limitations">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data limitations</div>
          <div className="mt-2 space-y-1.5">
            {data.data_limitations.map((w, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />{w}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FinancialPicture() {
  const { data, isError } = useQuery({
    queryKey: ["profit-intel"],
    queryFn: async () => (await api.get("/profit-intelligence/summary")).data,
    retry: false,
  });
  if (isError || !data) return null; // flag off / no permission => hidden
  const rev = data.revenue, costs = data.costs, con = data.contribution;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="financial-picture-section">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">Financial picture</h3>
        <span data-testid="fp-expenses-quality" className={`rounded-full px-3 py-1 text-xs font-semibold ${DQ[data.data_quality.expenses]}`}>Expense data: {data.data_quality.expenses}</span>
      </div>
      <p className="mb-4 text-xs text-slate-400">Based on recorded data · {data.period.label}. Actual revenue is Net Sales before GST; collected is cash net of refunds.</p>
      <div className="grid gap-3 sm:grid-cols-5">
        <Mini label="Actual revenue" value={money(rev.billed)} testid="fp-billed" />
        <Mini label="Collected" value={money(rev.collected)} testid="fp-collected" />
        <Mini label="Outstanding" value={money(rev.outstanding)} testid="fp-outstanding" />
        <Mini label="Recorded costs" value={money(costs.recorded)} testid="fp-costs" />
        <Mini label="Recorded contribution" value={money(con.value)} testid="fp-contribution" />
      </div>
      {con.status !== "complete" && (
        <div data-testid="fp-incomplete-note" className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
          {con.note || "This is a recorded contribution, not complete net profit — not all costs are recorded."}
        </div>
      )}
      {(data.warnings || []).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="fp-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-slate-500"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />{w.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}
